import { memo, useRef } from 'react';
import css from './SearchGrid.module.scss';
import { useLocalization } from '../../../context/Localization/localization';
import { CTAButton } from '../../ButtonBase';
import { GMButtonGroup } from '../../ButtonGroup';
import { GMSizedIcon } from '../../Icon';
import { GoMotoComponent } from '../../../API/GoMoto.types';
import { SearchGridProps, SearchGridRow } from './SearchGrid.types';
import { GMGrid } from '../../Grid';
import { SearchGridAppointmentTile } from './SearchGridAppointmentTile/SearchGridAppointmentTile';
import { SearchGridAppointmentTileProps } from './SearchGridAppointmentTile/SearchGridAppointmentTile.types';
import { Spinner } from '../../Spinner/Spinner';
import { usePagination } from '../../../helpers/Hooks/Pagination/Pagination';
import { LocalizationType } from '../../../context/Localization/localization.types';

/**
 * A grid that takes in a specific array of objects to then map to tiles in the grid and
 * paginate based on the height available before a scrollbar would appear.
 *
 * @param filters - {@link SearchGridProps.filters filters} description.
 * @param noResultsLabel - {@link SearchGridProps.noResultsLabel noResultsLabel} description.
 * @param gridTiles - {@link SearchGridProps.gridTiles gridTiles} description.
 */
export const SearchGrid: GoMotoComponent<SearchGridProps> = memo(({
    filters,
    noResultsLabel,
    gridTiles,
}) => {
    const Localization: LocalizationType = useLocalization();

    const buttonRef = useRef<HTMLDivElement>(null);
    const filterRef = useRef<HTMLDivElement>(null);

    const { setNextPage, setPreviousPage, startIndex, endIndex, nextEnabled, previousEnabled, shouldShow, isInitialized, pageRowCallbackRef } = usePagination({
        totalItems: gridTiles !== undefined ? gridTiles.length * 3 : 0,
        initialPageSize: 15,
        initialPage: 0,
        containerElement: document.getElementById('scrollable-body'),
        gridElement: document.getElementById('search-grid'),
        additionalElementsInContainer: [
            document.getElementById('scrollable-body-heading'),
            [...document.getElementsByClassName('MuiTabs-root')][0] as HTMLElement | null,
            filterRef.current,
        ],
        additionalElementsInGrid: [buttonRef.current],
    });

    return (
        <GMGrid
            container
            xs={12}
            className={css.searchGrid}
        >
            <GMGrid xs={12} ref={filterRef}>
                {filters}
            </GMGrid>
            {gridTiles === undefined || ((!isInitialized || !shouldShow) && gridTiles !== undefined && gridTiles.length !== 0) ? (
                <GMGrid xs={12}>
                    <Spinner />
                </GMGrid>
            ) : null}
            {isInitialized && gridTiles !== undefined && gridTiles.length !== 0 ? (
                <>
                    <GMGrid
                        id={'search-grid'}
                        container
                        xs={12}
                        rowGap={'2vmin'}
                        sx={{
                            visibility: shouldShow ? 'visible' : 'hidden',
                        }}
                    >
                        {gridTiles.slice(startIndex / 3, endIndex / 3)
                            .map((gridRows: SearchGridRow, outerIndex: number) => {
                                return (
                                    <GMGrid
                                        key={`appointment-tile-wrapper-outer-${outerIndex}`}
                                        container
                                        xs={12}
                                        columnGap={'1vmin'}
                                        wrap={'nowrap'}
                                    >
                                        {gridRows.map((gridTile: SearchGridAppointmentTileProps, innerIndex: number) => {
                                            return (
                                                <SearchGridAppointmentTile
                                                    key={`appointment-tile-inner-${innerIndex}`}
                                                    {...gridTile}
                                                    ref={pageRowCallbackRef}
                                                />
                                            );
                                        })}
                                    </GMGrid>
                                );
                            })}
                    </GMGrid>
                    <GMGrid
                        xs={12}
                        ref={buttonRef}
                        className={css.navigationButtonsRow}
                        sx={{ visibility: shouldShow ? 'visible' : 'hidden' }}
                    >
                        <GMButtonGroup
                            className={css.navigationButtons}
                            buttons={[
                                (previousEnabled || !shouldShow ? (
                                    <CTAButton
                                        styleType={'secondary'}
                                        type={'button'}
                                        label={Localization.Previous}
                                        onClick={setPreviousPage}
                                        kioskOptions={{
                                            inlineOptions: {
                                                leftIcon: true,
                                                sizedIcon: (
                                                    <GMSizedIcon
                                                        iconSize={'medium'}
                                                        iconType={'Arrow-Back'}
                                                        ariaLabel={Localization.BackArrow}
                                                    />
                                                ),
                                            },
                                        }}
                                        id={Localization.Previous}
                                        ariaLabel={Localization.Previous}
                                    />
                                ) : null),
                                (nextEnabled || !shouldShow ? (
                                    <CTAButton
                                        styleType={'secondary'}
                                        type={'button'}
                                        label={Localization.Next}
                                        onClick={setNextPage}
                                        kioskOptions={{
                                            inlineOptions: {
                                                sizedIcon: (
                                                    <GMSizedIcon
                                                        iconSize={'medium'}
                                                        iconType={'Arrow'}
                                                        ariaLabel={Localization.ProceedArrow}
                                                    />
                                                ),
                                            },
                                        }}
                                        id={Localization.Next}
                                        ariaLabel={Localization.Next}
                                    />
                                ) : null),
                            ]}
                        />
                    </GMGrid>
                </>
            ) : null}
            {gridTiles !== undefined && gridTiles.length === 0 ? (
                <GMGrid xs={12}>
                    {noResultsLabel}
                </GMGrid>
            ) : null}
        </GMGrid>
    );
});
